import { getBgmBehavior } from '@ps/bridge/qc/node/game-play'
import _ from 'lodash'
import assets from '@ps/bridge/qc/assets'
import { getFileExtension, getFileName } from '@com/utils/file'
import {
  isAudio,
  isDragonBone,
  isEmptyNode,
  isGroup,
  isImage,
  isNodeGhost,
  isParticle,
  isSequenceFrame,
  isTempNode,
  isText,
  isVideo,
} from '@ps/bridge/qc/base'
import { getDragonBoneDirectory } from '@ps/bridge/qc/node/dragon-bone'
import { getXlzDirectory } from '@ps/bridge/qc/node/xlz'
import * as videoScript from '@ps/bridge/qc/scripts/video'
import * as audioScript from '@ps/bridge/qc/scripts/audio'
import * as languageScript from '@ps/bridge/qc/scripts/language'
import SparkMD5 from 'spark-md5'
import { getDescendants, getDescendantsText } from '@ps/bridge/qc/node/utils'
import * as qc from 'qc'
import gameIframe from '@ps/bridge/iframe/iframe'
import nodePool from '@ps/bridge/qc/node-pool'
import { LanguageScript } from '@ps/bridge/qc/scripts/language'
import { getBgImgUrl, getBgmAudioUrl } from '@ps/workbench/global-config'
// import { getCurrentActionBinFiles } from '@ps/bridge/qc/scripts/movie-clip'

export type INodeConfig = {
  [uuid: string]: {
    // 原始资源，字符串或对象，如果是序列帧或粒子，是对象；如果是项目相对路径的地址，需要沿用相对路径，因为项目可能会被复制到其他账号中
    original_resource: string
    // 当前资源
    resource: string
    // 缩略图
    thumbnail: string
  }
}

export type IBgConfig = {
  // 0 无背景图，1 有背景图
  value: number
}

export type IBgmConfig = {
  // 0 无背景音乐，1 有背景音乐
  value: number
}

export type IEditorConfig = {
  nodeConfig: INodeConfig
  resources: []
  bg: IBgConfig
  bgm: IBgmConfig
  storyboard: {
    // layout: {}
  }
}

export type IResource = {
  // type:
  //   | 'image' // 图片
  //   | 'audio' // 音频
  //   | 'video' // 视频
  //   | 'sequenceFrame' // 序列帧
  //   | 'particle' // 粒子
  //   | 'dragonBone' // 龙骨
  //   | 'gameConfig' // 玩法
  //   | '3DModel'; // 3D模型
  //   | 'text' // 文本
  type: string
  name: string // 资源名字，对应 pt 上的分类标题
  path: string[] // 资源链接，不同的资源 path 的值会有差异，详细的内容在代码下面
  uuid: string // 对应节点 uuid
  // type === text 时才会有这个属性
  properties?: {
    refKeys: string[] // 关联 key 列表，用于在 pt 上找到在 languages.json 中对应的文本
    // 后端使用 refId 判定是否有是关联节点，如果存在 >= 两个文本节点的 refId 一样，说明这些节点是关联节点，在 pt 上需要联动更新
    refId: string // 格式是 ${md5(node.name)}___${md5(node.text)}，
  }
}

// export type ITextResource = {
//   type: 'text'; // 文本
//   name: string; // 资源名字，对应 pt 上的分类标题
//   path: string[]; // 格式是 ${md5(node.name)}___${md5(node.text)}
//   uuid: string; // 对应节点 uuid
//   properties: {
//     refKeys: string[]; // 关联 key 列表，用于在 pt 上找到在 languages.json 中对应的文本
//     // 后端使用 refId 判定是否有是关联节点，如果存在 >= 两个文本节点的 refId 一样，说明这些节点是关联节点，在 pt 上需要联动更新
//     refId: string; // 格式是 ${md5(node.name)}___${md5(node.text)}，
//   }
// }

const resourceTypes = {
  image: 'image',
  video: 'video',
  audio: 'audio',
  sequenceFrame: 'sequenceFrame',
  // 粒子节点也使用 image 类型
  particle: 'image',
  text: 'text',
  dragonBone: 'dragonBone',
}

/**
 * 获取文本节点的默认语言的竖屏文本值
 */
const getTextValueFromNode = (node: qc.Node, language: string) => {
  const config = languageScript.getScript(node)
  // 横竖屏文本值一样，这里取竖屏（pdef）的配置
  return _.get(config, ['langConfig', language, 'pdef', 'value'])
}

/**
 * 获取文件节点的 ref key
 * 用 md5(节点 name)___md5(节点文本值)___md5(节点样式) 作为 key
 */
const getTextRefKeyFromNode = (
  node: qc.Node,
  language: string,
): string | void => {
  const textValue = getTextValueFromNode(node, language)
  const langConf = languageScript.getScript(node) as LanguageScript
  // 获取节点所有语言和尺寸的样式配置集合
  const langStyleArr: any[] = []
  // 获取节点的样式配置，把非样式的属性都置空再做对比，由于属性可能都存在不一样，因此只提取 style 属性到 langStyleArr 中再做对比
  _.cloneDeepWith(langConf.langConfig, (value, key, parent) => {
    // @ts-ignore
    if (parent && parent.style && key === 'style') {
      // 如果含有 style 属性，代表是 pdef 或 ldef 的配置，把非 style 的属性置空,
      langStyleArr.push(value)
    }
  })
  if (_.isUndefined(textValue)) {
    console.error('找不到文本值，无法生成文本引用 key')
  } else {
    // 用节点 name + 节点文本值的 md5 值作为 key
    return `${SparkMD5.hash(node.name)}___${SparkMD5.hash(
      textValue,
    )}___${SparkMD5.hash(JSON.stringify(langStyleArr))}`
  }
}

type ITextRef = {
  [uuid: string]: string[]
}

/**
 * 获取文本节点关联配置（什么是关联配置，节点名字与节点默认语言的竖屏的值一样的即判定为关联配置）
 * @param gamePlay
 * @param language {string}
 * @returns {{[key: string]: string[]}} uuid 列表
 */
const getTextNodeRef = ({
  gamePlay,
  language,
}: {
  gamePlay: qc.Node
  language: string
}) => {
  const textNodes = getDescendantsText(gamePlay, false)
  const textRef: ITextRef = {}
  textNodes.forEach((node) => {
    const key = getTextRefKeyFromNode(node, language)
    if (key) {
      // 值必须是 uuid 的列表，这样才可以通过 uuid 找到 languages.json 中的配置
      textRef[key] = [...(textRef[key] || []), node.uuid]
    }
  })
  return textRef
}

/**
 * 从素材资产列表中提取音频资源
 */
function pickAudioResourceFromAssets(): IResource[] {
  const audioList = (
    Object.values(assets.uuid2UrlConf).filter((path) =>
      /(\.mp3\.bin|\.mp3)$/i.test(path),
    ) || []
  ).map((audio) => {
    return audio.replace(/\.bin$/, '').replace(/^\//, '')
  })
  const resources: IResource[] = []
  if (!_.isEmpty(audioList)) {
    // 过滤 bm_bgm.mp3 与 bm_bgm0.mp3
    const whiteList = audioList.filter(
      (item) =>
        Boolean(item) && /\.mp3$/.test(item) && !/\/bm_bgm0?\.mp3$/.test(item),
    )

    if (!_.isEmpty(whiteList)) {
      whiteList.forEach((item) => {
        resources.push({
          type: resourceTypes.audio,
          name: getFileName(item, true),
          path: [_.trimStart(item, '/')],
          uuid: '',
        })
      })
    }
  }
  return resources
}

export async function getCommonResources({
  bg,
  bgm,
  isPickAudioResourceFromAssets,
}: {
  bg: IBgConfig
  bgm: IBgmConfig
  isPickAudioResourceFromAssets: boolean
}) {
  const bgConfig = getBgmBehavior()
  const hasBg = bg.value === 1
  const hasBgm = bgConfig && Boolean(bgConfig.bgmAudioUrl)
  let commonResources = []

  let bgImgUrl = getBgImgUrl(false)
  if (hasBg && bgImgUrl) {
    bgImgUrl = _.trimStart(bgImgUrl, '/')
    commonResources.push({
      type: resourceTypes.image,
      name: 'pb_bg',
      path: [bgImgUrl],
      uuid: '',
    })
  }
  let bgmUrl = getBgmAudioUrl(false)
  if (hasBgm && bgmUrl) {
    bgmUrl = _.trimStart(bgmUrl, '/')
    commonResources.push({
      type: resourceTypes.audio,
      name: 'bm_bgm',
      path: [bgmUrl],
      uuid: '',
    })
  }

  // pt 模板转 ps 的项目，不会为音频增加对应的节点，所以需要从素材资产列表中提取音频资源
  if (isPickAudioResourceFromAssets) {
    commonResources = [...commonResources, ...pickAudioResourceFromAssets()]
  }

  return {
    type: 'common',
    name: 'common',
    children: commonResources,
  }
}

/**
 * https://confluence.mobvista.com/pages/viewpage.action?pageId=62742083
 * https://confluence.mobvista.com/pages/viewpage.action?pageId=62741277
 */
function getResources({
  nodes,
  textRef,
  language,
}: {
  nodes: qc.Node[]
  textRef: ITextRef
  language: string
}): IResource[] {
  return _.uniq(
    _.flatMapDepth(
      nodes
        .map((node) => {
          if (isText(node)) {
            const refKey = getTextRefKeyFromNode(node, language)
            const langConf = languageScript.getScript(node) as LanguageScript
            if (!langConf || !langConf.langKey) {
              console.warn(
                `节点 ${node.name} 的语言配置不存在或者语言配置中没有 langKey，请检查`,
              )
              return
            }
            if (refKey && langConf && langConf.langKey) {
              // 文本区分横竖屏
              return {
                type: resourceTypes.text,
                // 转换到 pt 时，path[0]会用作分类的 prefix，如果有横竖屏，在 pt 要获取 languages.json 数据时要在 prefix
                // 的基础上加 _V、_H 才可访问到文本配置，这里必须是 uuid，因为 languages.json 中的 key 为 uuid
                path: [node.uuid],
                // 在 pt 上用作分类的名字
                name: node.name,
                uuid: node.uuid,
                // 在 properties 存储关联节点，这样在 pt 上才可以通过 properties 找到关联节点并联动更新
                // 同一个场景的关联文本节点会去重，不同场景的不去重
                properties: {
                  // refKeys 用于在 pt 上找到在 languages.json 中对应的文本
                  refKeys: textRef[refKey],
                  // 后端使用 refId 判定是否有是关联节点，如果存在 >= 两个文本节点的 refId 一样，说明这些节点是关联节点，在 pt 上需要联动更新
                  refId: refKey,
                },
              }
            }
          }
          if (isDragonBone(node)) {
            let dir = getDragonBoneDirectory(node)
            if (dir) {
              // 龙骨返回序列帧路径
              dir = _.trimStart(dir, '/')
              return {
                type: resourceTypes.dragonBone,
                path: [dir],
                name: node.name,
                uuid: node.uuid,
              }
            }
            console.warn(node, '找不到序龙骨的文件路径信息')
          }
          if (isSequenceFrame(node)) {
            // 序列帧返回序列帧路径
            let dir = getXlzDirectory(node)
            if (dir) {
              dir = _.trimStart(dir, '/')
              return {
                type: resourceTypes.sequenceFrame,
                path: [dir],
                name: node.name,
                uuid: node.uuid,
              }
            }
            console.warn(node, '找不到序列帧的文件路径信息')
          }
          if (isVideo(node)) {
            const conf = videoScript.getScript(node)
            if (!conf) {
              console.warn(node, '无视频脚本')
              return
            }
            // 视频可能存在单独的音频
            const videoUrl = _.trimStart(conf.videoUrl || '', '/')
            if (videoUrl) {
              const res = [
                {
                  path: [videoUrl],
                  type: resourceTypes.video,
                  name: node.name,
                  uuid: node.uuid,
                },
              ]
              const audioUrl = _.trimStart(conf.audioUrl || '', '/')
              // audioUrl 可以为空
              if (audioUrl) {
                res.push({
                  path: [audioUrl],
                  type: resourceTypes.audio,
                  name: node.name,
                  uuid: node.uuid,
                })
              } else {
                console.warn(node, '视频的 audio url is empty')
              }
              return res
            }
            console.warn(node, '视频的 video url is empty')
          }
          if (isAudio(node)) {
            const conf = audioScript.getScript(node)
            if (!conf) {
              console.warn(node, '无音频脚本')
              return
            }
            const audioUrl = _.trimStart(conf.audioUrl || '', '/')
            if (audioUrl) {
              return {
                type: resourceTypes.audio,
                name: node.name,
                uuid: node.uuid,
                path: [audioUrl],
              }
            }
            console.warn(node, '音频节点 audio url is empty')
          }
          // 如果粒子存在多张图片不将其转为一个组，而是单独作为一张图片，如果需要将其转为一个组，可以将粒子放在一个组节点下
          if (isImage(node) || isParticle(node)) {
            const url = _.get(node, 'texture.atlas.url')
            const imgSrc = _.get(node, 'texture.atlas.img.currentSrc')
            const ext = getFileExtension(imgSrc)
            if (url) {
              const path = _.trimStart(url.replace(/\.bin$/, `.${ext}`), '/')
              return {
                type: isImage(node)
                  ? resourceTypes.image
                  : resourceTypes.particle,
                name: node.name,
                uuid: node.uuid,
                path: [path],
              }
            }
            console.warn(node, 'image url is empty')
          }
        })
        .filter((item) => Boolean(item)) as IResource[],
    ),
  )
}

const getNodesWhichHadResource = (nodes: qc.Node[]) => {
  return nodes.reduce((accumulator: qc.Node[], node, index) => {
    const list = getDescendants(node, (node) => {
      // 过滤不需要的节点，这些节点不需要生成资源信息
      // 非文本临时节点的其他临时节点需要过滤
      if (
        (isTempNode(node) && !isText(node)) ||
        isNodeGhost(node) ||
        isEmptyNode(node)
      ) {
        return false
      }
      if (
        isDragonBone(node) ||
        isParticle(node) ||
        isImage(node) ||
        isSequenceFrame(node) ||
        isVideo(node) ||
        isAudio(node) ||
        isText(node)
      ) {
        return true
      }
      return false
    })
    return _.uniqBy([...accumulator, ...list], 'uuid')
  }, [])
}

/**
 * 文本节点配置是否一致，名字和默认语言的竖屏的文本值一样或 langKey 一样, 另外文本不同语言下的样式都一致，才能说明是同一个文本节点
 * langKey 一样说明是从 pt 通用模板转 ps 的素材包（只有这种类型的包才会存在 langKey 一样，自研的目的是为了复用同一份配置）
 */
const isEqualTextNode = (node1: qc.Node, node2: qc.Node, lang: string) => {
  // 名字和默认语言的竖屏的文本值一样的话，则去重
  const text1 = getTextValueFromNode(node1, lang)
  const text2 = getTextValueFromNode(node2, lang)
  const langConf1 = languageScript.getScript(node1) as LanguageScript
  const langConf2 = languageScript.getScript(node2) as LanguageScript
  return (
    (text1 &&
      text2 &&
      node1.name === node2.name &&
      text1 === text2 &&
      langConf1.langConfig &&
      langConf2.langConfig &&
      JSON.stringify(langConf2.langConfig) ===
        JSON.stringify(langConf1.langConfig)) ||
    (langConf1 && langConf2 && langConf1.langKey === langConf2.langKey)
  )
}

/**
 * 序列帧资源配置是否一样，序列帧的资源个数、名字与排序都一样
 * 暂时注释，先不用删除
 */
// const isEqualXlzNode = (node1, node2) => {
//   if (getCurrentActionBinFiles(node1) && getCurrentActionBinFiles(node2)) {
//     const frame1 = getCurrentActionBinFiles(node1)
//       .split(',')
//       .map(item => getFileName(item))
//     const frame2 = getCurrentActionBinFiles(node2)
//       .split(',')
//       .map(item => getFileName(item))
//     return _.isEqual(frame1, frame2)
//   }
//   return false
// }

export function getSceneResources({
  scene,
  defaultLanguage,
}: {
  scene: qc.Node
  defaultLanguage: string
}) {
  const children = scene.children
  if (_.isEmpty(children)) {
    return null
  }

  const textRef = getTextNodeRef({
    gamePlay: gameIframe.gamePlay,
    language: defaultLanguage,
  })

  const isEqual = (value: IResource, other: IResource) => {
    // 名字和默认语言的竖屏的文本值一样的话，则去重。这样同一个场景在 pt 上就不会展示重复的文本配置
    if (
      value.type === resourceTypes.text &&
      other.type === resourceTypes.text
    ) {
      const node = nodePool.find(value.uuid)
      const otherNode = nodePool.find(other.uuid)
      return isEqualTextNode(node, otherNode, defaultLanguage)
    }

    // 如果两个序列帧的资源个数、名字与排序都一样的话，则去重
    // 展示先注释，isEqualXlzNode 先不用删除
    // if (
    //   value.type === resourceTypes.sequenceFrame &&
    //   other.type === resourceTypes.sequenceFrame
    // ) {
    //   const node = nodePool.find(value.uuid)
    //   const otherNode = nodePool.find(other.uuid)
    //   return isEqualXlzNode(node, otherNode)
    // }

    return _.isEqual(value.path, other.path)
  }

  const nodesOutOfGroup = getNodesWhichHadResource(
    children.filter((node) => !isGroup(node)) || [],
  )
  // 去重一样的资源
  const resourcesOutOfGroup = _.uniqWith(
    getResources({
      nodes: nodesOutOfGroup,
      textRef,
      language: defaultLanguage,
    }),
    isEqual,
  )

  // 找出第一层级的组节点
  const groups = children.filter((node) => isGroup(node))
  const groupResources = groups.map((group) => {
    // 组内相同资源去重
    let innerChildren =
      _.uniqWith(
        getResources({
          nodes: getNodesWhichHadResource(group.children) || [],
          textRef,
          language: defaultLanguage,
        }),
        isEqual,
      ) || []
    // 提取不存在组外资源列表的资源（因为同一资源不需要同时展示在组内和组外）
    innerChildren = innerChildren.filter((item) =>
      resourcesOutOfGroup.every((j) => !isEqual(j, item)),
    )
    return {
      name: group.name + '-' + group.uuid.split('-')[0], // 避免 PS 的组自动联动
      uuid: group.uuid,
      type: 'group',
      children: innerChildren,
    }
  })

  return {
    type: 'scene',
    // 场景的名字不是用的 name 属性，而是 uniqueName
    name: scene.uniqueName,
    uuid: scene.uuid,
    children: [...resourcesOutOfGroup, ...groupResources],
  }
}
